import React, { useState } from "react";
import {
  Container,
  TextField,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Fade,
  Chip,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  SupportAgent as SupportAgentIcon,
  HelpOutline as HelpOutlineIcon,
  Security as SecurityIcon,
  RateReview as InquiryIcon,
  VerifiedUser as VerifiedIcon,
} from "@mui/icons-material";
import ResponsiveAppBar from "./AppBar";
import Footer from "./Footer";

const PageWrapper = styled(Box)({
  backgroundColor: "#f8fafc",
  minHeight: "100vh",
  color: "#1e293b",
});

const HeroBanner = styled(Box)({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #eff6ff 100%)",
  color: "#1e293b",
  padding: "60px 20px 80px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "20%",
    width: "60%",
    height: "100%",
    background: "radial-gradient(circle, rgba(119, 200, 199, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
});

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    loanType: "Gold Loan",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const scrollToForm = () => {
    const formElement = document.getElementById("inquiry-form-section");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const serviceId = "service_8uw6rpl";
    const templateId = "template_ts49asm";
    const publicKey = "cixd4nyfgjX2Zi3Jp";

    import("emailjs-com")
      .then((emailjs) => {
        emailjs
          .send(serviceId, templateId, formData, publicKey)
          .then(() => {
            setSubmitted(true);
            setIsLoading(false);
            setTimeout(() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                phone: "",
                loanType: "Gold Loan",
                message: "",
              });
            }, 5000);
          })
          .catch((error) => {
            console.error("EmailJS Error:", error);
            setIsLoading(false);
            // Even if EmailJS quota or config fails, provide friendly feedback
            setSubmitted(true);
          });
      })
      .catch((error) => {
        console.error("Error importing emailjs:", error);
        setIsLoading(false);
        setSubmitted(true);
      });
  };

  const contactCards = [
    {
      icon: <EmailIcon sx={{ fontSize: 28, color: "#936097" }} />,
      title: "Email Inquiries",
      detail: "poovarasan@velavanfinance.com",
      sub: "We respond within 24 business hours",
      action: () => window.open("mailto:poovarasan@velavanfinance.com"),
      buttonText: "Send Email",
    },
    {
      icon: <InquiryIcon sx={{ fontSize: 28, color: "#77c8c7" }} />,
      title: "Online Inquiry Support",
      detail: "Fast Online Processing",
      sub: "Submit your details for loan & chit assistance",
      action: scrollToForm,
      buttonText: "Fill Inquiry Form",
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 28, color: "#0d9488" }} />,
      title: "Working Hours",
      detail: "Mon - Sat: 9:00 AM - 6:00 PM",
      sub: "Active customer service and processing",
      action: scrollToForm,
      buttonText: "Inquire Now",
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 28, color: "#936097" }} />,
      title: "100% Confidential",
      detail: "Bank-Grade Privacy",
      sub: "Your details and inquiries remain protected",
      action: scrollToForm,
      buttonText: "Get Started",
    },
  ];

  return (
    <PageWrapper>
      <ResponsiveAppBar />

      {/* Hero Header */}
      <HeroBanner>
        <Container maxWidth="md">
          <Chip
            icon={<SupportAgentIcon sx={{ color: "#77c8c7 !important" }} />}
            label="Dedicated Customer Support"
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              color: "#77c8c7",
              border: "1px solid rgba(119, 200, 199, 0.3)",
              fontWeight: 600,
              mb: 2.5,
              fontSize: "0.85rem",
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "3rem" },
              mb: 2,
              letterSpacing: "0.5px",
            }}
          >
            Get in Touch With Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(17, 19, 23, 0.8)",
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Have a question about Gold Loans, Vehicle Financing, or your account? Our financial experts are ready to assist you.
          </Typography>
        </Container>
      </HeroBanner>

      {/* Quick Contact Action Cards */}
      <Container maxWidth="xl" sx={{ mt: -6, position: "relative", zIndex: 10, mb: 8 }}>
        <Grid container spacing={3}>
          {contactCards.map((card, idx) => (
            <Grid item xs={12} sm={6} lg={3} key={idx}>
              <Card
                sx={{
                  borderRadius: "20px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 20px 40px rgba(119, 200, 199, 0.18)",
                    borderColor: "#77c8c7",
                  },
                }}
              >
                <CardContent sx={{ p: 3.5 }}>
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "14px",
                      backgroundColor: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: "1.05rem",
                      mb: 0.5,
                      color: "#0f172a",
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                      mb: 0.5,
                      fontSize: "0.92rem",
                    }}
                  >
                    {card.detail}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b",
                      display: "block",
                      mb: 2.5,
                    }}
                  >
                    {card.sub}
                  </Typography>

                  <Button
                    onClick={card.action}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "rgba(147, 96, 151, 0.3)",
                      color: "#936097",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 600,
                      textTransform: "none",
                      py: 0.8,
                      "&:hover": {
                        borderColor: "#77c8c7",
                        backgroundColor: "rgba(119, 200, 199, 0.08)",
                        color: "#0f766e",
                      },
                    }}
                  >
                    {card.buttonText} →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Main Content Area: Form & Support Details */}
      <Container maxWidth="xl" sx={{ pb: 12 }}>
        <Grid container spacing={5}>
          {/* Left Form */}
          <Grid item xs={12} lg={7}>
            <Paper
              id="inquiry-form-section"
              elevation={0}
              sx={{
                p: { xs: 3.5, sm: 5 },
                borderRadius: "24px",
                border: "1px solid rgba(226, 232, 240, 0.9)",
                boxShadow: "0 10px 40px rgba(0,0,0,0.04)",
                backgroundColor: "#ffffff",
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1,
                  }}
                >
                  Send Us an Inquiry
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  Fill out the form below and one of our loan advisors will contact you shortly.
                </Typography>
              </Box>

              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputLabelProps={{ sx: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputLabelProps={{ sx: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputLabelProps={{ sx: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Loan Type of Interest"
                        name="loanType"
                        value={formData.loanType}
                        onChange={handleChange}
                        variant="outlined"
                        InputLabelProps={{ sx: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      >
                        <MenuItem value="Gold Loan">Gold Loan (Jewel)</MenuItem>
                        <MenuItem value="Vehicle Loan">Two / Four Wheeler Loan</MenuItem>
                        <MenuItem value="Chit Scheme">Chit & Savings Scheme</MenuItem>
                        <MenuItem value="General Inquiry">General / Account Support</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Message / Loan Requirements"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Please share any specific details about the loan amount or queries you have..."
                        variant="outlined"
                        InputLabelProps={{ sx: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        startIcon={
                          isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SendIcon />
                          )
                        }
                        sx={{
                          py: 1.6,
                          borderRadius: "12px",
                          background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                          color: "white",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontWeight: 700,
                          fontSize: "1rem",
                          textTransform: "none",
                          boxShadow: "0 6px 20px rgba(119, 200, 199, 0.35)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #7e4d82 0%, #5eb8b7 100%)",
                            boxShadow: "0 8px 25px rgba(119, 200, 199, 0.5)",
                          },
                        }}
                      >
                        {isLoading ? "Sending Your Inquiry..." : "Submit Inquiry"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <Fade in={submitted}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      px: 3,
                      backgroundColor: "rgba(16, 185, 129, 0.08)",
                      borderRadius: "16px",
                      border: "2px solid rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 64, color: "#10b981", mb: 2 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#065f46",
                        fontFamily: "'Playfair Display', serif",
                        mb: 1,
                      }}
                    >
                      Inquiry Received Successfully!
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#047857",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        maxWidth: 450,
                        mx: "auto",
                      }}
                    >
                      Thank you for contacting Velavan Finance. A dedicated loan officer will review your details and reach out to you within 24 hours.
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Paper>
          </Grid>

          {/* Right Info & FAQ Sidebar */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
              {/* Working Hours & Office Box */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(147, 96, 151, 0.25)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    color: "#ffffff",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <AccessTimeIcon sx={{ color: "#77c8c7" }} />
                  Customer Support Hours
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, fontSize: "0.92rem", color: "rgba(226, 232, 240, 0.85)" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", pb: 1, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <span>Monday - Friday</span>
                    <span className="font-semibold text-white">9:00 AM - 6:00 PM</span>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", pb: 1, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <span>Saturday</span>
                    <span className="font-semibold text-white">9:00 AM - 5:00 PM</span>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Sunday</span>
                    <span className="font-semibold text-rose-400">Closed (Weekly Holiday)</span>
                  </Box>
                </Box>
              </Paper>

              {/* Safety & Trust Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <SecurityIcon sx={{ color: "#936097", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      color: "#0f172a",
                    }}
                  >
                    Guaranteed Security
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    lineHeight: 1.7,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  All customer applications, documents, and communication are encrypted. Collaterals like gold jewels are maintained in high-security, insured bank-grade vault facilities.
                </Typography>
              </Paper>

              {/* Quick Application Checklist */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <HelpOutlineIcon sx={{ color: "#77c8c7", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      color: "#0f172a",
                    }}
                  >
                    Application Guidelines
                  </Typography>
                </Box>

                <Box component="ul" sx={{ pl: 2.5, m: 0, color: "#64748b", fontSize: "0.88rem", lineHeight: 1.8 }}>
                  <li>Apply online directly for instant Gold Loan & Vehicle Loan processing.</li>
                  <li>Inquire via form or email for Chit & Savings fund scheme allocations.</li>
                  <li>Receive transparent terms and fast funds transfer directly.</li>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </PageWrapper>
  );
};

export default ContactPage;